import { memo, useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import SharedClipPageHeader from './SharedClipPageHeader';
import SharedClipErrorPage from './SharedClipErrorPage';
import { useAppContext } from '@/context/AppContext/AppContext';
import { ErrorContainerProps } from '@/App/ErrorBoundary/ErrorContainer';
import ClipPlayer from '@/Pages/Clip/ClipPlayer/ClipPlayer';
import { ClipsContextError, useClipsContext } from '@/context/ClipsContext/ClipsContext';
import ClipPlayerWrapper from '@/Pages/Clip/ClipPlayer/ClipPlayerWrapper';
import ClipPlayerV2 from '@/App/remotion/MainPlayer/ClipPlayerV2';
import { shouldUseNewRecordingsPlayer } from '@/stores/clip';
import { core } from '@/stores/core';

const SharedClipPageInternal: React.FC = memo(() => {
  const { clipData } = useClipsContext();
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);

  const useNewPlayer = useMemo(() => {
    return shouldUseNewRecordingsPlayer({
      event_type: coreStore.content?.event_type,
      live_end_time: coreStore.content?.live_end_time,
      individual_recording_status: coreStore.content?.individual_recording_status || 'UNAVAILABLE',
      isSharedPage: true,
      player_config: clipData.asset_metadata.player_config
    });
  }, [
    coreStore.content?.event_type,
    coreStore.content?.live_end_time,
    coreStore.content?.individual_recording_status,
    clipData.asset_metadata.player_config
  ]);

  useEffect(() => {
    if (clipData.title) {
      document.title = `${clipData.title} - Goldcast Content Lab`;
    } else {
      document.title = 'Goldcast Content Lab';
    }
  }, [clipData.title]);

  return (
    <div className="fixed inset-0 z-40 flex h-full w-full flex-col bg-slate-100">
      <SharedClipPageHeader clip={clipData} />
      <div className="flex w-full justify-between" style={{ height: 'calc(100% - 63px)' }}>
        {useNewPlayer ? (
          <ClipPlayerV2 canEditCaptions={false} playerClasses="pr-0" />
        ) : (
          <ClipPlayer playerClasses="lg:p-16" />
        )}
      </div>
    </div>
  );
});

const SharedClipPage = () => {
  const { adminAppRouter } = useAppContext();
  const clipId = adminAppRouter.currentRoute?.value?.params?.clipId;

  const renderErrorComponent = useCallback((error: ClipsContextError, clipId: string) => {
    let newErrorObject: ErrorContainerProps = {
      errorMessage: error.message,
      errorDisplayCode: error.statusCode
    };

    if (error?.statusCode === 404 || error?.statusCode === 401) {
      newErrorObject = { errorMessage: `Clip with ID "${clipId}" not found.`, errorDisplayCode: 404 };
    } else {
      newErrorObject = { errorMessage: 'Something went wrong on our end.' };
    }

    return <SharedClipErrorPage error={newErrorObject} />;
  }, []);

  const handleTranscriptError = useCallback(() => {
    throw new Error('Failed to fetch transcript');
  }, []);

  return (
    <div className="content-lab-root">
      <ClipPlayerWrapper
        clipId={clipId}
        handleTranscriptError={handleTranscriptError}
        shared
        showLoaderOnPage
        renderErrorComponent={renderErrorComponent}
      >
        <SharedClipPageInternal />
      </ClipPlayerWrapper>
    </div>
  );
};

export default SharedClipPage;
